/**
 * Shared rules for the "promote an existing user" pickers.
 *
 * Two dialogs offer the same choice — pick a company user and give them a role. `NewTrainerDialog`
 * ("הוספת מאמן") grants TRAINER; `AddOwnerDialog` grants ADMIN and backs "מנהל חדש" as well as the
 * group-admin flows. They had drifted: one searched, the other was a plain dropdown. Keeping the
 * matching and eligibility rules here means fixing one picker fixes both.
 */

/** The shape both pickers actually read. Structural on purpose, so a `User` DTO satisfies it. */
export type UserCandidate = {
  id?: number | string;
  name?: string;
  email?: string;
  enabled?: boolean;
  authorities?: string[];
};

/**
 * Candidate matching for the picker. Matches NAME **or** EMAIL.
 *
 * MUI's default filter stringifies each option via `getOptionLabel`, which returns the name only, so
 * typing an email produced "לא נמצאו משתמשים" even when that user was loaded — indistinguishable
 * from a broken search. Substring rather than prefix, because staff search by whatever fragment they
 * remember ("gmail.com", a surname); case-insensitive and trimmed because a pasted address arrives
 * with both surprises.
 */
export const filterUserCandidates = <T extends UserCandidate>(
  options: readonly T[],
  inputValue: string = "",
): T[] => {
  const query = (inputValue || "").trim().toLowerCase();
  if (!query) return [...options];
  return options.filter(
    (option) =>
      (option?.name || "").toLowerCase().includes(query) ||
      (option?.email || "").toLowerCase().includes(query),
  );
};

/**
 * Drops users who cannot actually take the role being granted.
 *
 * <p>Two rules, and both exist for the same reason: **a candidate the picker offers but the server
 * will refuse is a dead end**, and the failure only surfaces after someone has filled in the form.
 *
 * <ol>
 *   <li><b>Disabled users.</b> A disabled user has no session — the auth filter refuses to build one
 *       (`UserOrCompanyIsDisabledException`) — so promoting them produces staff who cannot log in.</li>
 *   <li><b>Users who already hold a disqualifying role</b>, passed by the caller. The `/admin/user`
 *       pool takes a single `withoutRole`, so asking it for non-ADMINs still returns the company's
 *       existing MANAGERs; `createManager` then refuses them for already holding the role.</li>
 * </ol>
 *
 * <p>**The server remains the boundary.** `UserService.getCompanyUsersWithoutRole` applies the
 * enabled rule to both the `/admin/user` and `/system/company/{id}/users` pools so they cannot
 * drift, and the create endpoints refuse an ineligible promotion outright. This is the client
 * declining to offer a choice that would fail, not the client enforcing anything.
 *
 * <p>Tests `!== false` rather than `=== true` deliberately. A payload that omits `enabled` should
 * fall through as eligible, the way it did before this rule existed; treating "absent" as "disabled"
 * would empty the picker wholesale the next time a DTO drops the field — the loud-but-wrong failure
 * this shape exists to avoid. Same reasoning for a missing `authorities`.
 */
export const promotableCandidates = <T extends UserCandidate>(
  users: readonly T[] | null | undefined,
  disqualifyingRoles: readonly string[] = [],
): T[] =>
  (users || []).filter(
    (user) =>
      user?.enabled !== false &&
      !(user?.authorities || []).some((role) => disqualifyingRoles.includes(role)),
  );
